import React, { useCallback } from 'react';

import { Box, Chip, Stack, TextField, Typography } from '@mui/material';

import { CreateCampaignPayload } from '~/services/AccommodationService';

import { parseStrictInteger } from './campaignNumberInput';

export type CampaignDefaultOccupancy = NonNullable<CreateCampaignPayload['defaultOccupancy']>;

interface Props {
  value: CampaignDefaultOccupancy;
  onChange: (next: CampaignDefaultOccupancy) => void;
}

/**
 * Editor for a campaign's `defaultOccupancy` — the informational/merchandised occupancy shown
 * for this campaign (not an eligibility restriction, unlike Bundle's min/max hotel rules).
 */
function CampaignOccupancyEditor({ value, onChange }: Props) {
  const updateField = useCallback(
    (key: 'children' | 'infants' | 'teenagers', next: number | undefined) => {
      const { [key]: _removed, ...rest } = value;
      onChange(next === undefined ? rest : { ...rest, [key]: next });
    },
    [value, onChange],
  );

  const addChildAge = useCallback(
    (age: number) => {
      onChange({ ...value, childrenAges: [...(value.childrenAges ?? []), age] });
    },
    [value, onChange],
  );

  const removeChildAge = useCallback(
    (index: number) => {
      const nextAges = (value.childrenAges ?? []).filter((_, i) => i !== index);
      onChange(nextAges.length > 0 ? { ...value, childrenAges: nextAges } : { ...value, childrenAges: undefined });
    },
    [value, onChange],
  );

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <TextField
          label="Adults"
          type="number"
          value={value.adults}
          onChange={(e) => {
            onChange({ ...value, adults: parseStrictInteger(e.target.value, 1) ?? 1 });
          }}
          inputProps={{ min: 1 }}
          size="small"
          sx={{ width: 120 }}
        />
        <TextField
          label="Children"
          type="number"
          value={value.children ?? ''}
          onChange={(e) => updateField('children', parseStrictInteger(e.target.value, 0))}
          inputProps={{ min: 0 }}
          size="small"
          sx={{ width: 120 }}
        />
        <TextField
          label="Infants"
          type="number"
          value={value.infants ?? ''}
          onChange={(e) => updateField('infants', parseStrictInteger(e.target.value, 0))}
          inputProps={{ min: 0 }}
          size="small"
          sx={{ width: 120 }}
        />
        <TextField
          label="Teenagers"
          type="number"
          value={value.teenagers ?? ''}
          onChange={(e) => updateField('teenagers', parseStrictInteger(e.target.value, 0))}
          inputProps={{ min: 0 }}
          size="small"
          sx={{ width: 120 }}
        />
      </Stack>

      <Box>
        <TextField
          label="Children ages"
          type="number"
          placeholder="Type age and press Enter"
          size="small"
          fullWidth
          onKeyDown={(e) => {
            if (e.key !== 'Enter') {
              return;
            }
            e.preventDefault();
            const input = e.target as HTMLInputElement;
            const age = parseStrictInteger(input.value, 0);
            if (age !== undefined) {
              addChildAge(age);
              input.value = '';
            }
          }}
        />
        {(value.childrenAges ?? []).length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
            {(value.childrenAges ?? []).map((age, index) => (
              <Chip
                key={`${age}-${index}`}
                label={age}
                size="small"
                variant="outlined"
                onDelete={() => removeChildAge(index)}
              />
            ))}
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        Informational occupancy shown on merchandising surfaces — not an eligibility restriction.
      </Typography>
    </Stack>
  );
}

export default CampaignOccupancyEditor;
